import { DefaultError, useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import MessagesHistory from 'src/components/ChatAgent/MessagesHistory'
import { ChatInput } from 'src/components/inputs/ChatInput'
import TTSPlayer from 'src/components/TTSPlayer'
import { CAI_CHAT_FLOWS } from 'src/constants/cai-chat.constant'
import QUERY_KEYS from 'src/constants/queryKey'
import { NotificationContext } from '../../../context/NotificationContext'
import CAIIcon from '../../CaiIcon'
import { CAIIconAnim } from '../../CaiIcon/CAIIconAnim'
import Tooltip from '../../UI/Tooltip'
import SubmitButton from './SubmitButton'

type Message = {
  message: string
  type: string
  images: string[]
  time: string
  owner: string
  avatar: any
}

type Data = {
  chat_id: string
  hidden: boolean
  id: string
  sender: string
  text: string
  timestamp: string
}

function Session(props: any) {
  const {
    setFlow,
    messages,
    awaitingResponse,
    setMessagesMap,
    agentScope,
    ...rest
  } = props

  const { t, i18n } = useTranslation()

  const {
    sessionId,
    setSessionId,
    handleCompleteActivity,
    isSubmitted,
    setIsSubmitted,
    isInActive,
    setIsInActive,
  } = useContext(NotificationContext)

  const { data = [], isLoading } = useQuery<
    { data: Data[] },
    DefaultError,
    Message[]
  >({
    queryKey: [QUERY_KEYS.SESSION, sessionId, i18n.language],
    queryFn: ({ signal }) =>
      fetch(
        `${process.env.REACT_APP_CARCH_ENDPOINT}/sessions/${sessionId}/messages?language=${i18n.language}`,
        {
          signal,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        },
      )
        .then((r) => r.json())
        .catch((e: unknown) => {
          throw new Error(JSON.stringify(e))
        }),
    select: ({ data }) =>
      data
        .filter((i) => !i.hidden)
        .map((i) => ({
          message: i.text,
          type: i.sender === 'user' ? 'sent' : 'received',
          images: [],
          time: new Date(i.timestamp).toLocaleTimeString(),
          owner: i.sender,
          avatar: CAIIcon,
        })),
  })

  const handleClose = () => {
    setFlow(CAI_CHAT_FLOWS.DEFAULT)
    setMessagesMap((state: any) => ({ ...state, session: [] }))
    setSessionId('')
    setIsInActive(false)
  }

  const handleOnMessage = async (message: string, hidden: boolean) => {
    try {
      await rest.onMessage(message, hidden)
    } finally {
      setIsSubmitted(true)
    }
  }

  useEffect(() => {
    setMessagesMap((state: any) => ({ ...state, session: data }))
  }, [JSON.stringify(data)])

  if (isLoading) return <CAIIconAnim />

  return (
    <div
      className="d-flex flex-column card card-chat-body border-0 order-1 w-100 position-relative h-100 p-3 gap-2 overflow-hidden"
      style={{
        background: 'var(--card-color)',
      }}
    >
      <div
        className="h-100 position-relative d-flex justify-content-end align-items-start  overflow-hidden flex-column"
        style={{
          minHeight: 0,
        }}
      >
        <MessagesHistory
          awaitingResponse={awaitingResponse}
          id="msgSessionHistory"
          messages={messages}
        />

        <div className="d-flex gap-2 position-absolute top-0 end-0 align-items-center">
          <TTSPlayer messages={messages} />

          {/* close button */}
          {!handleCompleteActivity && (
            <Tooltip message="Return to chat mode.">
              <button
                className="btn btn-danger p-1 text-light d-flex"
                style={{ height: 24 }}
                onClick={handleClose}
              >
                <i className="icofont-close" style={{ marginTop: 1 }}></i>
              </button>
            </Tooltip>
          )}
        </div>

        {!isSubmitted && (
          <SubmitButton
            disabled={messages.length <= 1}
            onMessage={handleOnMessage}
          />
        )}
      </div>

      <ChatInput
        {...rest}
        awaitingResponse={awaitingResponse}
        disabled={isInActive}
      />
    </div>
  )
}

export default Session
